/* oxlint-disable import/max-dependencies -- boot composition module: it wires the Task 1-8 pieces (poll/unpack/supervisor/user-home/workbench) into the two role pipelines; splitting the wiring would scatter the boot sequence */
/**
 * `conveyor-agent boot` — the vnext pod boot entry. Composes the tested Task
 * 1-8 pieces into two role pipelines (agent / workbench) and the legacy-launch
 * tombstone predicate the CLI guards on. The pod runs the BAKED agent — there
 * is no runtime self-update (removed after it caused the 2026-07-19 eviction
 * outage); a new agent version ships via publish → auto-rebake.
 *
 * The bash entrypoint (runtime/entrypoint.sh) is retired: a split-only pod
 * runs `conveyor-agent boot` in each container, keyed by CONVEYOR_CONTAINER_ROLE.
 * Every network/spawn/fs seam is injected via `BootDeps` (production defaults in
 * `defaultBootDeps`) so the dispatch tests run offline.
 */
import { spawn } from "node:child_process";
import { statSync } from "node:fs";
import { hostname as osHostname } from "node:os";
import { readAgentVersion } from "../runner/session-runner-helpers.js";
import { workbenchPort } from "../workbench/mode.js";
import { DEFAULT_WORKBENCH_PORT } from "../workbench/protocol.js";
import { buildChildEnv, decodeJwtClaims } from "./bundle.js";
import { computeGraphifyEnv } from "./graphify.js";
import { createBootLogger } from "./logger.js";
import { runPipeline } from "./pipeline.js";
import { pollBootstrapBundle } from "./poll.js";
import { defaultExec, type ExecDeps } from "./exec.js";
import { superviseRunner } from "./supervisor.js";
import type { BootContext, BootLogger, BootRole, BootStep } from "./types.js";
import { reclaimHomeOwnership, wireUserHome } from "./user-home.js";
import { runWorkbenchBoot } from "./workbench-boot.js";
import { defaultWorkbenchBootDeps, type WorkbenchBootDeps } from "./workbench-boot-deps.js";

const HOME_DIR = "/home/conveyor";
const USER_HOME_MOUNT = "/mnt/conveyor-users";

// Leaf module so cli.ts's guard doesn't eagerly load the whole boot graph.
export { isLegacyEntrypointLaunch, TOMBSTONE_MESSAGE } from "./tombstone.js";

/** The single injectable seam bundle — network (fetch/poll), spawn, exec, and
 *  identity — so dispatch/pipeline composition runs without side effects. */
export interface BootDeps {
  fetchFn: typeof fetch;
  sleepFn: (ms: number) => Promise<void>;
  spawnFn: typeof spawn;
  exec: ExecDeps["exec"];
  dirExists: (path: string) => boolean;
  hostname: string;
  log: BootLogger;
}

export function defaultBootDeps(): BootDeps {
  return {
    fetchFn: fetch,
    sleepFn: (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      }),
    spawnFn: spawn,
    exec: defaultExec,
    dirExists: (path) => {
      try {
        return statSync(path).isDirectory();
      } catch {
        return false;
      }
    },
    hostname: osHostname(),
    log: createBootLogger(),
  };
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Resolves when `signal` aborts (or is already aborted); never rejects. */
function whenAborted(signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    signal.addEventListener("abort", () => resolve(), { once: true });
  });
}

/**
 * The agent container's boot: poll bootstrap → unpack the bundle into the
 * runner's child env (with graphify env merged) → reclaim home ownership
 * (best-effort) → wire the persistent user-home. superviseRunner runs after
 * the pipeline — it IS the container's foreground process, not a step. Each
 * post-poll step no-ops when the bundle is absent (SIGTERM aborted standby).
 */
export function agentBootSteps(
  deps: BootDeps = defaultBootDeps(),
  signal: AbortSignal = new AbortController().signal,
): BootStep[] {
  // Started alongside the bootstrap poll (it only touches local fs ownership,
  // ~0.7s of chown) so the two overlap; the reclaimHome step awaits it. Runs
  // even when standby is SIGTERM-aborted — a chown of home is harmless.
  let reclaim: Promise<void> | undefined;
  return [
    {
      name: "pollBootstrap",
      critical: true,
      run: async (ctx) => {
        reclaim = reclaimHomeOwnership(HOME_DIR, deps.exec, ctx.log).catch(() => undefined);
        const bundle = await pollBootstrapBundle(ctx.apiUrl, ctx.bootstrapToken, {
          fetchFn: deps.fetchFn,
          sleepFn: deps.sleepFn,
          log: ctx.log,
          signal,
        });
        if (bundle) ctx.bundle = bundle;
      },
    },
    {
      name: "unpackBundle",
      critical: true,
      run: (ctx) => {
        if (ctx.bundle) {
          const claims = decodeJwtClaims(ctx.bundle.sessionJwt);
          ctx.claims = claims;
          const childEnv = buildChildEnv({
            base: process.env,
            bundle: ctx.bundle,
            claims,
            hostname: ctx.hostname,
            log: ctx.log,
          });
          const graphifyEnv = computeGraphifyEnv(
            process.env,
            ctx.bundle.gitPlan.repoName,
            deps.dirExists,
          );
          ctx.childEnv = { ...childEnv, ...graphifyEnv };
        }
        return Promise.resolve();
      },
    },
    {
      name: "reclaimHome",
      critical: false,
      run: async (ctx) => {
        if (!ctx.bundle) return;
        // Already in flight since the poll started — this step is just the
        // barrier so wireUserHome never runs on unreclaimed ownership.
        await (reclaim ?? reclaimHomeOwnership(HOME_DIR, deps.exec, ctx.log));
      },
    },
    {
      name: "wireUserHome",
      critical: true,
      run: (ctx) => {
        if (ctx.bundle) {
          wireUserHome({
            home: HOME_DIR,
            userHomeMount: USER_HOME_MOUNT,
            userId: ctx.childEnv?.CONVEYOR_USER_ID,
            projectId: ctx.childEnv?.CONVEYOR_PROJECT_ID ?? ctx.childEnv?.PROJECT_ID,
            sharedDir: process.env.CONVEYOR_SHARED_DIR,
            log: ctx.log,
          });
        }
        return Promise.resolve();
      },
    },
  ];
}

/**
 * Declarative manifest of the workbench container's critical boot path
 * (poll → unpack → startDaemon), pinned by boot-dispatch.test.ts. The PRODUCTION
 * executor is `runWorkbenchBoot` (Task 7): it runs these three critical
 * operations AND the post-listen deferred jobs (ensure /workspaces/repo, git
 * prep, user-home, sshd, reclaim) that a flat pipeline cannot express. This
 * function documents the contract those three steps must honor; the run bodies
 * delegate to the same deps seam so it stays a faithful (if partial) executor.
 */
export function workbenchBootSteps(
  deps: WorkbenchBootDeps = defaultWorkbenchBootDeps(),
): BootStep[] {
  return [
    {
      name: "pollBootstrap",
      critical: true,
      run: async (ctx) => {
        const bundle = await deps.pollBundle(ctx);
        if (bundle) ctx.bundle = bundle;
      },
    },
    {
      name: "unpackBundle",
      critical: true,
      run: (ctx) => {
        if (ctx.bundle) ctx.childEnv = deps.unpack(ctx, ctx.bundle);
        return Promise.resolve();
      },
    },
    {
      name: "startDaemon",
      critical: true,
      run: async (ctx) => {
        await deps.startDaemon({
          port: workbenchPort() ?? DEFAULT_WORKBENCH_PORT,
          token: ctx.bootstrapToken,
          version: readAgentVersion() ?? "unknown",
          // The manifest has no GitPrepJob, so it must never claim readiness —
          // "pending" is the safe answer if anyone ever executes these steps
          // directly instead of via runWorkbenchBoot (which serves live state).
          getGitStatus: () => ({ t: "gitStatus", state: "pending" }),
        });
      },
    },
  ];
}

async function runAgentRole(
  ctx: BootContext,
  deps: BootDeps,
  signal: AbortSignal,
): Promise<number> {
  try {
    await runPipeline(agentBootSteps(deps, signal), ctx);
  } catch (err) {
    // A critical step (incl. the poll's 401 BootstrapUnauthorizedError, wrapped
    // by runPipeline) — the pipeline already logged FATAL. Pod identity invalid.
    ctx.log.error(`[boot] agent boot failed: ${errMsg(err)}`);
    return 1;
  }
  if (signal.aborted || !ctx.bundle) {
    ctx.log.info("[boot] SIGTERM during standby — exiting.");
    return 0;
  }
  // The pod runs whatever agent version was BAKED into the image — there is no
  // runtime self-update. A new agent ships via publish → auto-rebake → new
  // pods; a booting pod never downloads or version-checks the agent (the old
  // npm-preflight self-update caused the 2026-07-19 ephemeral-eviction outage
  // and is gone).
  // The agent container's /workspaces/repo is a dead overlay (entrypoint.sh:907)
  // — every workspace op is proxied to the workbench daemon; run from ~.
  process.chdir(HOME_DIR);
  // superviseRunner owns LAUNCH_MARKER (touched post-bind, before first spawn)
  // and the SIGTERM drain once the runner is live.
  return superviseRunner({
    childEnv: ctx.childEnv ?? {},
    cwd: HOME_DIR,
    apiUrl: ctx.apiUrl,
    bootstrapToken: ctx.bootstrapToken,
    log: ctx.log,
    spawnFn: deps.spawnFn,
    signal,
  });
}

async function runWorkbenchRole(
  ctx: BootContext,
  deps: BootDeps,
  signal: AbortSignal,
): Promise<number> {
  // The daemon runs the BAKED agent — no runtime self-update (see runAgentRole).
  // runWorkbenchBoot parks forever on success; the race resolves only when the
  // signal aborts (SIGTERM), which is our clean exit. Its internal poll gets the
  // same signal so a standby SIGTERM aborts the in-flight fetch too.
  try {
    await Promise.race([
      runWorkbenchBoot(ctx, {
        pollBundle: (workbenchCtx) =>
          pollBootstrapBundle(workbenchCtx.apiUrl, workbenchCtx.bootstrapToken, {
            fetchFn: deps.fetchFn,
            sleepFn: deps.sleepFn,
            log: workbenchCtx.log,
            signal,
          }),
      }),
      whenAborted(signal),
    ]);
  } catch (err) {
    ctx.log.error(`[boot] workbench boot failed: ${errMsg(err)}`);
    return 1;
  }
  // ctx.bundle distinguishes a serving workbench from one still in standby —
  // pod-death forensics reads this line.
  ctx.log.info(
    ctx.bundle
      ? "[boot] SIGTERM — workbench shutting down."
      : "[boot] SIGTERM during standby — exiting.",
  );
  return 0;
}

/**
 * The `conveyor-agent boot` process. Resolves the pod exit code (cli.ts does
 * `process.exit(await runBoot(...))`).
 *
 * - `--preflight-update` → NO-OP (exit 0). There is no runtime self-update any
 *   more: the pod runs the baked agent. Kept as an inert accepted flag only so
 *   an OLD baked shim that still invokes it can't fail the boot.
 * - else: CONVEYOR_CONTAINER_ROLE selects the pipeline (fatal-loud on anything
 *   but agent/workbench — vnext pods are split-only), after asserting the two
 *   required identity env vars are present.
 */
export async function runBoot(argv: string[], deps: BootDeps = defaultBootDeps()): Promise<number> {
  const { log } = deps;

  if (argv.includes("--preflight-update")) {
    log.info("[boot] --preflight-update is a no-op (baked agent, no runtime self-update)");
    return 0;
  }

  const role = process.env.CONVEYOR_CONTAINER_ROLE;
  if (role !== "agent" && role !== "workbench") {
    log.error("[boot] FATAL missing/invalid CONVEYOR_CONTAINER_ROLE — vnext pods are split-only");
    return 1;
  }

  const apiUrl = process.env.CONVEYOR_API_URL;
  const bootstrapToken = process.env.POD_BOOTSTRAP_TOKEN;
  if (!apiUrl || !bootstrapToken) {
    // Mirrors entrypoint.sh:161-162 — no identity, nothing to poll for.
    log.error("[boot] FATAL missing required env: CONVEYOR_API_URL + POD_BOOTSTRAP_TOKEN");
    return 1;
  }

  // One controller drives every abort path: standby poll (resolves null →
  // exit 0), the workbench park race, and the live runner drain.
  const controller = new AbortController();
  const abort = (): void => controller.abort();
  process.on("SIGTERM", abort);
  process.on("SIGINT", abort);

  const ctx: BootContext = {
    role: role as BootRole,
    apiUrl,
    bootstrapToken,
    hostname: deps.hostname,
    log,
  };

  const code = await (role === "workbench"
    ? runWorkbenchRole(ctx, deps, controller.signal)
    : runAgentRole(ctx, deps, controller.signal));
  // Settle any concurrent work still in flight (e.g. the workbench's poll
  // promise if the role returned via SIGTERM before it settled) — nothing may
  // outlive the role's verdict.
  controller.abort();
  return code;
}
